import React from 'react';
import StatTileNew from '../ui/StatTileNew';
import StatCard from '../ui/StatCard';
import SortableTable from '../SortableTable';

const IconChart = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" />
  </svg>
);
const IconVisits = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const IconEvents = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);
const IconClients = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconRatio = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 8v4l3 2" /><circle cx="12" cy="12" r="9" />
  </svg>
);

const DeltaCell = ({ value, percent }) => {
  const cls = value > 0 ? 'text-green-600 dark:text-green-400' : (value < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500');
  const icon = value > 0 ? (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
    </svg>
  ) : value < 0 ? (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  ) : null;

  return (
    <div className={`flex items-center font-medium ${cls}`}>
      {icon}
      <span className="mr-1">{value > 0 ? `+${value}` : value}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400">({percent > 0 ? `+${percent.toFixed(1)}` : percent.toFixed(1)}%)</span>
    </div>
  );
};

const formatYMD = (d) => new Date(d).toISOString().split('T')[0];

const StatisticsSummary = ({ summary, clientAggregates, toolAggregates, startDate, endDate, clientFilter, toolFilter }) => {
  const fmt = (n) => typeof n === 'number' ? n.toLocaleString('fr-FR') : n;

  const periodLabel = `${new Date(startDate).toLocaleDateString('fr-FR')} – ${new Date(endDate).toLocaleDateString('fr-FR')} · vs période précédente`;

  return (
    <>
      <StatCard icon={IconChart} title="Statistiques d'usage" subtitle={periodLabel}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTileNew
            label="Visites totales"
            icon={IconVisits}
            value={summary.totalVisits}
            previousValue={summary.previous?.totalVisits}
          />
          <StatTileNew
            label="Événements totaux"
            icon={IconEvents}
            value={summary.totalEvents}
            previousValue={summary.previous?.totalEvents}
          />
          <StatTileNew
            label="Clients actifs"
            icon={IconClients}
            value={summary.clients}
            previousValue={summary.previous?.clients}
          />
          <StatTileNew
            label="Événements/visite"
            icon={IconRatio}
            value={Number(summary.avgEventsPerVisit?.toFixed(1))}
            previousValue={summary.previous?.avgEventsPerVisit != null ? Number(summary.previous.avgEventsPerVisit.toFixed(1)) : null}
          />
        </div>
      </StatCard>

      {/* Consolidated by client */}
      <StatCard className="mt-6" icon={IconClients} title="Statistiques d'usage consolidées par client">
        <SortableTable
          data={clientAggregates.filter(r => !clientFilter || r.client === clientFilter.value)}
          columns={[
            { key: 'client', label: 'Client' },
            { key: 'visits', label: 'Visites', render: (item) => (<span className="text-sm text-gray-600 dark:text-gray-400">{fmt(item.visits)}</span>), sortFunction: (a, b, dir) => dir === 'ascending' ? a.visits - b.visits : b.visits - a.visits },
            {
              key: 'deltaVisits',
              label: 'Δ Visites',
              render: (item) => (<DeltaCell value={item.deltaVisits} percent={item.deltaVisitsPct} />),
              sortFunction: (a, b, dir) => dir === 'ascending' ? a.deltaVisits - b.deltaVisits : b.deltaVisits - a.deltaVisits
            },
            { key: 'events', label: 'Événements', render: (item) => (<span className="text-sm text-gray-600 dark:text-gray-400">{fmt(item.events)}</span>), sortFunction: (a, b, dir) => dir === 'ascending' ? a.events - b.events : b.events - a.events },
            {
              key: 'deltaEvents',
              label: 'Δ Événements',
              render: (item) => (<DeltaCell value={item.deltaEvents} percent={item.deltaEventsPct} />),
              sortFunction: (a, b, dir) => dir === 'ascending' ? a.deltaEvents - b.deltaEvents : b.deltaEvents - a.deltaEvents
            },
            { key: 'users', label: '# Utilisateurs', render: (item) => (<span className="text-sm text-gray-600 dark:text-gray-400">{fmt(item.users)}</span>), sortFunction: (a, b, dir) => dir === 'ascending' ? a.users - b.users : b.users - a.users },
            {
              key: 'deltaUsers',
              label: 'Δ Utilisateurs',
              render: (item) => (<DeltaCell value={item.deltaUsers} percent={item.deltaUsersPct} />),
              sortFunction: (a, b, dir) => dir === 'ascending' ? a.deltaUsers - b.deltaUsers : b.deltaUsers - a.deltaUsers
            }
          ]}
          initialSortBy={{ key: 'visits', direction: 'descending' }}
          exportable={true}
          disableFilters={true}
          exportFilename={`stats_clients_${formatYMD(startDate)}_${formatYMD(endDate)}.csv`}
          emptyMessage="Aucune donnée sur la période"
          withSum
        />
        <p className="text-sm text-gray-600 dark:text-gray-400">La somme des utilisateurs peut comptabiliser plusieurs fois un même utilisateur sur différents outils. A utiliser uniquement pour avoir un ordre de grandeur.</p>
      </StatCard>

      {/* Consolidated by tool */}
      <StatCard className="mt-6" icon={IconChart} title="Statistiques d'usage consolidées par outil">
        <SortableTable
          data={toolAggregates.filter(r => !toolFilter || r.tool === toolFilter.value)}
          columns={[
            { key: 'tool', label: 'Outil' },
            { key: 'visits', label: 'Visites', render: (item) => (<span className="text-sm text-gray-600 dark:text-gray-400">{fmt(item.visits)}</span>), sortFunction: (a, b, dir) => dir === 'ascending' ? a.visits - b.visits : b.visits - a.visits },
            {
              key: 'deltaVisits',
              label: 'Δ Visites',
              render: (item) => (<DeltaCell value={item.deltaVisits} percent={item.deltaVisitsPct} />),
              sortFunction: (a, b, dir) => dir === 'ascending' ? a.deltaVisits - b.deltaVisits : b.deltaVisits - a.deltaVisits
            },
            { key: 'events', label: 'Événements', render: (item) => (<span className="text-sm text-gray-600 dark:text-gray-400">{fmt(item.events)}</span>), sortFunction: (a, b, dir) => dir === 'ascending' ? a.events - b.events : b.events - a.events },
            {
              key: 'deltaEvents',
              label: 'Δ Événements',
              render: (item) => (<DeltaCell value={item.deltaEvents} percent={item.deltaEventsPct} />),
              sortFunction: (a, b, dir) => dir === 'ascending' ? a.deltaEvents - b.deltaEvents : b.deltaEvents - a.deltaEvents
            },
            { key: 'users', label: '# Utilisateurs', render: (item) => (<span className="text-sm text-gray-600 dark:text-gray-400">{fmt(item.users)}</span>), sortFunction: (a, b, dir) => dir === 'ascending' ? a.users - b.users : b.users - a.users },
            {
              key: 'deltaUsers',
              label: 'Δ Utilisateurs',
              render: (item) => (<DeltaCell value={item.deltaUsers} percent={item.deltaUsersPct} />),
              sortFunction: (a, b, dir) => dir === 'ascending' ? a.deltaUsers - b.deltaUsers : b.deltaUsers - a.deltaUsers
            }
          ]}
          initialSortBy={{ key: 'visits', direction: 'descending' }}
          exportable={true}
          disableFilters={true}
          exportFilename={`stats_outils_${formatYMD(startDate)}_${formatYMD(endDate)}.csv`}
          emptyMessage="Aucune donnée sur la période"
          withSum
        />
        <p className="text-sm text-gray-600 dark:text-gray-400">Un utilisateur passant par Cockpit est comptabilisé en double sur les outils intégrés au Cockpit.</p>
      </StatCard>
    </>
  );
};

export default StatisticsSummary;


